/**
 * Deferred deep-link infrastructure.
 *
 * Notifications and shared URLs sometimes target a protected route while the
 * user is logged out. The protected route guards redirect them to the login
 * page, losing the original URL. This helper persists the intended URL in
 * sessionStorage so the login page can restore it after a successful auth.
 *
 * Per-portal keys prevent admin/HR/voice collisions when multiple desks are open.
 *
 * NOTE: The "finance" portal is intentionally still supported here so that
 * finance notifications generated in the standalone Finance Desk project
 * (which shares the same Supabase backend) round-trip cleanly. This project
 * itself no longer renders any /finance routes.
 *
 * The "voice" portal targets the standalone HIBANE Voice Agent portal at
 * /voice. It reuses the admin auth stack but is gated to admins with
 * voice_agent.* permission.
 */

export type Portal = "admin" | "hr" | "finance" | "voice" | "socials";

const KEYS: Record<Portal, string> = {
  admin: "pending_deep_link_admin",
  hr: "pending_deep_link_hr",
  finance: "pending_deep_link_finance",
  voice: "pending_deep_link_voice",
  socials: "pending_deep_link_socials",
};

const TTL_MS = 10 * 60 * 1000; // 10 minutes

interface Envelope {
  url: string;
  ts: number;
}

const portalPrefixes = (portal: Portal): string[] => {
  if (portal === "admin") return ["/admin"];
  if (portal === "hr") return ["/hr/"];
  if (portal === "voice") return ["/voice"];
  if (portal === "socials") return ["/socials"];
  // Finance Desk is a separate project; its in-app paths are root-level.
  return ["/dashboard", "/login", "/finance/"];
};

const loginPath = (portal: Portal): string => {
  if (portal === "admin") return "/admin/login";
  if (portal === "hr") return "/hr/login";
  if (portal === "voice") return "/voice/login";
  if (portal === "socials") return "/socials/login";
  return "/login";
};

const startsWithAnyPrefix = (portal: Portal, url: string): boolean =>
  portalPrefixes(portal).some((p) => url === p.replace(/\/$/, "") || url.startsWith(p));

const isWorthSaving = (portal: Portal, url: string): boolean => {
  if (!startsWithAnyPrefix(portal, url)) return false;
  if (url === loginPath(portal) || url.startsWith(`${loginPath(portal)}?`)) return false;

  const hasQuery = url.includes("?");
  if (hasQuery) return true;

  if (portal === "admin") {
    return url !== "/admin" && url !== "/admin/";
  }
  if (portal === "hr") {
    return url !== "/hr" && url !== "/hr/" && url !== "/hr/dashboard" && url !== "/hr/dashboard/";
  }
  if (portal === "voice") {
    return url !== "/voice" && url !== "/voice/";
  }
  if (portal === "socials") {
    return url !== "/socials" && url !== "/socials/";
  }
  return (
    url !== "/finance" &&
    url !== "/finance/" &&
    url !== "/finance/dashboard" &&
    url !== "/finance/dashboard/" &&
    url !== "/dashboard" &&
    url !== "/dashboard/"
  );
};

const safeStorage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

export function savePendingDeepLink(portal: Portal, url: string): void {
  const storage = safeStorage();
  if (!storage) return;
  if (!url || !isWorthSaving(portal, url)) return;

  const env: Envelope = { url, ts: Date.now() };
  try {
    storage.setItem(KEYS[portal], JSON.stringify(env));
  } catch {
    // ignore quota / privacy errors
  }
}

function read(portal: Portal): string | null {
  const storage = safeStorage();
  if (!storage) return null;

  const raw = storage.getItem(KEYS[portal]);
  if (!raw) return null;

  try {
    const env = JSON.parse(raw) as Envelope;
    if (!env?.url || typeof env.ts !== "number") {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    if (Date.now() - env.ts > TTL_MS) {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    if (!startsWithAnyPrefix(portal, env.url)) {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    if (env.url === loginPath(portal) || env.url.startsWith(`${loginPath(portal)}?`)) {
      storage.removeItem(KEYS[portal]);
      return null;
    }
    return env.url;
  } catch {
    try {
      storage.removeItem(KEYS[portal]);
    } catch { /* noop */ }
    return null;
  }
}

export function peekPendingDeepLink(portal: Portal): string | null {
  return read(portal);
}

export function consumePendingDeepLink(portal: Portal): string | null {
  const url = read(portal);
  const storage = safeStorage();
  if (storage) {
    try { storage.removeItem(KEYS[portal]); } catch { /* noop */ }
  }
  return url;
}

export function clearPendingDeepLink(portal: Portal): void {
  const storage = safeStorage();
  if (!storage) return;
  try { storage.removeItem(KEYS[portal]); } catch { /* noop */ }
}
